// THE ROUND — what every fan-out reports about its own rounds, extended by each lane's
// payload and never emitted on its own.
// Split out of `../hive-event.ts`, which is now the barrel over this directory.

import { z } from 'zod';
import { count, HIVE_EVENT_MAX_ITEMS, line } from './bounds';

/**
 * WHAT EVERY FAN-OUT REPORTS ABOUT ITS OWN ROUNDS. Extended by each lane's payload; never
 * emitted on its own.
 *
 * The numbers here are the ones `docs/idea/hive.md` asks a lane to prove about itself, and
 * every one of them is a RATE with its denominator beside it: `lead_idle_ms` over
 * `round_ms` is the fraction of the parallelism a fan-out bought and then spent waiting
 * (rule 8); `dispatched >= 2` is rule 5; `steerable` over `dispatched` is rule 9's channel;
 * `handoffs_unrouted` is rule 10's silent drop.
 *
 * SUMS OVER `rounds`, NEVER AVERAGES, and never a single round's index. This was the plan
 * sketch's one structural error: it named a per-round `round: int >= 1`, but the scout
 * emits ONE event per SURVEY summing every round it ran (`hiveRoundTiming`,
 * `apps/runner/src/lead-round.ts`), so a round
 * index has no value to carry there. `rounds` says how many rounds these sums fold, which
 * is the number a reader needs to divide by and the one an average would have hidden.
 */
export const HiveRoundPayloadSchema = z.object({
  /** How many rounds these sums fold. `1` for a fan-out with no rounds of its own. */
  rounds: z
    .number()
    .int()
    .min(1)
    .describe('hive.md rule 8: rounds folded into these sums. The denominator of every ms below.'),
  /** Members the lead PLANNED for those rounds. */
  members: count().describe(
    'hive.md rule 1: members the lead partitioned the work into. The plan, before dispatch.',
  ),
  /**
   * Members actually dispatched. Below {@link members} when a member never started — a
   * claim conflict, a drain. This is the denominator of every per-member rate on the
   * event, and rule 5's own measurement: `dispatched === 1` is a fan-out that bought a
   * context boundary and a return path that can fail, and nothing else.
   */
  dispatched: count().describe(
    'hive.md rule 5: members actually dispatched. 1 is strictly worse than 0; 0 or >= 2 is the rule.',
  ),
  /** Dispatched members that did not come back with an answer. */
  failed: count().describe(
    'hive.md rule 2: dispatched members whose return was not read. Numerator over `dispatched`.',
  ),
  /**
   * The round's own verdict, recorded rather than re-derived. It is what
   * `scripts/team-run-funnel.ts` reads off a `team_fanout` row to tell a healthy fan-out
   * from one that lost a member, and a consumer re-deriving it from the counts would be a
   * second definition of "healthy" that can disagree with the producer's.
   */
  ok: z
    .boolean()
    .describe('the round settled with every member read and the lead itself unfaulted'),
  /** Dispatch to every member settled, summed over {@link rounds}. */
  round_ms: count().describe('hive.md rule 8: dispatch -> every member settled, summed.'),
  /** Wall clock the lead spent working BESIDE those rounds. `0` when no lead job is wired. */
  lead_ms: count().describe(
    'hive.md rule 8: wall clock the lead worked beside the round. 0 is a lead that idled, not a missing field.',
  ),
  /** `round_ms - lead_ms`, floored at 0 — the idle rule 8 names. */
  lead_idle_ms: count().describe(
    'hive.md rule 8: the parallelism this fan-out bought and then spent waiting. Over `round_ms` it is THE rate.',
  ),
  /**
   * Wall clock the lead added PAST the round it was told to race. Rule 8's promise is that
   * a lead's work is FREE; this field is that promise being broken, and it is reported
   * rather than absorbed into `round_ms`.
   */
  lead_overran_ms: count().describe(
    "hive.md rule 8: wall clock the lead added past the round. Rule 8's promise is that this is 0.",
  ),
  /**
   * Why the lead's own round work threw, one bounded line per round. EMPTY is the healthy
   * answer and is always emitted — a lead job that faulted costs itself and never the
   * round, but a round that reports healthy while its lead faulted is the silence rule 8's
   * measurement exists to end.
   */
  lead_work_failed: z
    .array(line())
    .max(HIVE_EVENT_MAX_ITEMS)
    .describe('hive.md rule 8: the lead job faulted. Empty = it did not; never omitted.'),
  /** Dispatched members that had a live mailbox a lead could correct them on. */
  steerable: count().describe(
    'hive.md rule 9: members reachable while alive. 0 with `dispatched > 0` is a channel that was never wired.',
  ),
  /** Corrections the lead actually SENT into those mailboxes. Producer: plan 102 slice 03. */
  steered: count().describe(
    'hive.md rule 9: corrections the lead SENT. `steerable` is the denominator; 0 is a mouth nobody used.',
  ),
  /** Premises of the lead's own brief that a member disproved. */
  falsified: count().describe(
    'hive.md rule 11: premises the members disproved. A COUNT OF PREMISES, not of members, so it is NOT bounded by `dispatched` — one member can disprove several, and prod measured 219 against 175 dispatched on the scout lane (2026-09-03). A reader that renders it as a percentage of `dispatched` will exceed 100% and is right to. The text rides the brief, not the chain.',
  ),
  /** Members that read the checkout and concluded there was nothing to do. */
  no_work_found: count().describe(
    'hive.md rule 12: members that disproved their own question. Beside `failed`, never folded into it.',
  ),
  /** Cross-member obligations the lead routed to the member that owns the file. */
  handoffs_routed: count().describe(
    'hive.md rule 10: handoffs the lead routed to an owning member.',
  ),
  /** Cross-member obligations that reached no member at all. */
  handoffs_unrouted: count().describe(
    'hive.md rule 10: handoffs that reached nobody. Non-zero is work this run surfaced and dropped.',
  ),
});
export type HiveRoundPayload = z.infer<typeof HiveRoundPayloadSchema>;
